import {
    generateScopeReport,
    IExportBase,
    IScope,
    ISignedScopeDocument,
    updateScope,
} from "@/api";
import { Box, Menu, MenuItem, ToggleButton } from "@mui/material";
import { ScopeViewType } from "../types";

import { ActionButton } from "@/components/shared/buttons/ActionButton";
import { MetadataChip } from "@/components/shared/chips/MetadataChip";
import { PageHeader } from "@/components/shared/layout/PageHeader";
import { Modal } from "@/components/shared/Modal";
import { StyledToggleButtonGroup } from "@/components/shared/StyledToggleButtonGroup";
import { TextField } from "@/components/shared/TextField";
import { theme } from "@/config/project";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    BookCheck,
    DownloadIcon,
    Edit,
    Factory,
    File,
    MessageSquare,
    MoreVertical,
    Umbrella,
} from "lucide-react";
import React from "react";
import { ExportScopeModal } from "../../../../components/frontend/ExportScopeModal";

interface IProps {
    /**
     * The scope to display
     */
    scope: IScope;
    signedScopeDocument?: ISignedScopeDocument;
    onChatOpen?: () => void;
    /**
     * Current view type (Overview or Details)
     */
    viewType: ScopeViewType;
    /**
     * Callback to change the view type
     */
    setViewType: (viewType: ScopeViewType) => void;
    /**
     * Whether the scope is currently being processed (hides view toggle)
     */
    isProcessing?: boolean;
    /**
     * Whether the scope processing has failed (hides view toggle)
     */
    isFailed?: boolean;
}

/**
 * Displays the scope toolbar which contains the scope title, clause_checklist, uploaded time, and actions
 */
export const ScopeToolbar = ({
    scope,
    signedScopeDocument,
    onChatOpen,
    viewType,
    setViewType,
    isProcessing = false,
    isFailed = false,
}: IProps) => {
    const [renameModalOpen, setRenameModalOpen] = React.useState(false);
    const [newName, setNewName] = React.useState("");
    const [exportModalOpen, setExportModalOpen] = React.useState(false);
    const [moreMenuAnchor, setMoreMenuAnchor] =
        React.useState<null | HTMLElement>(null);
    const queryClient = useQueryClient();
    const { settings: organizationSettings } = useOrganization();

    const renameMutation = useMutation({
        mutationFn: (newName: string) =>
            updateScope(scope.id, { document_title: newName }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["frontend-scope", String(scope.id)],
            });
            setRenameModalOpen(false);
            setNewName("");
        },
    });

    const handleExport = () => {
        setExportModalOpen(true);
    };

    const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMoreMenuAnchor(event.currentTarget);
    };

    const handleMoreMenuClose = () => {
        setMoreMenuAnchor(null);
    };

    const handleRenameClick = () => {
        setNewName(scope.document_title);
        setRenameModalOpen(true);
        handleMoreMenuClose();
    };

    const handleRename = () => {
        if (newName.trim() && newName !== scope.document_title) {
            renameMutation.mutate(newName.trim());
        }
    };

    const handleExportSubmit = async (exportData: IExportBase) => {
        try {
            const blob = await generateScopeReport(scope.id, exportData);

            // Download the file
            const extension = "xlsx";
            const mimeType =
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

            const sanitizedTitle = scope.document_title.replace(
                /\.[^/.]+$/,
                ""
            );

            const url = window.URL.createObjectURL(
                new Blob([blob], { type: mimeType })
            );
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${sanitizedTitle}.${extension}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setExportModalOpen(false);
        } catch (error) {
            console.error("Export failed:", error);
        }
    };

    const handleViewTypeChange = (
        _event: React.MouseEvent<HTMLElement>,
        newValue: ScopeViewType | null
    ) => {
        if (newValue !== null) {
            setViewType(newValue);
        }
    };

    const subheadContent = (
        <Box
            sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-end",
                flexWrap: "wrap",
            }}
        >
            <Box>
                <Box
                    sx={{
                        fontSize: "0.65rem",
                        color: theme.palette.text.secondary,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.025em",
                        mb: 0.25,
                    }}
                >
                    Document
                </Box>
                <MetadataChip
                    icon={<File />}
                    label={
                        scope.document.document_name.split(".pdf")[0] + ".pdf"
                    }
                    backgroundColor={theme.palette.tertiary.main}
                    color={theme.palette.primary.main}
                    hoverBackgroundColor={theme.palette.primary.main}
                    href={signedScopeDocument?.signed_url}
                />
            </Box>
            <Box>
                <Box
                    sx={{
                        fontSize: "0.65rem",
                        color: theme.palette.text.secondary,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.025em",
                        mb: 0.25,
                    }}
                >
                    Checklist
                </Box>
                <MetadataChip
                    icon={<BookCheck />}
                    label={scope.clause_checklist.name}
                    backgroundColor={theme.palette.successLight.main}
                    color={theme.palette.success.main}
                    hoverBackgroundColor={theme.palette.success.main}
                    href={`/organizations/checklists/${scope.clause_checklist_id}`}
                />
            </Box>

            {/* Industry */}
            {scope.industry && (
                <Box>
                    <Box
                        sx={{
                            fontSize: "0.65rem",
                            color: theme.palette.text.secondary,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.025em",
                            mb: 0.25,
                        }}
                    >
                        Industry
                    </Box>
                    <MetadataChip
                        icon={<Factory />}
                        label={scope.industry.name}
                        backgroundColor={"#fef3c7"}
                        color={"#d97706"}
                        hoverBackgroundColor={"#d97706"}
                    />
                </Box>
            )}

            {/* Carrier */}
            {scope.document.carrier && (
                <Box>
                    <Box
                        sx={{
                            fontSize: "0.65rem",
                            color: theme.palette.text.secondary,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.025em",
                            mb: 0.25,
                        }}
                    >
                        Carrier
                    </Box>
                    <MetadataChip
                        icon={<Umbrella />}
                        label={scope.document.carrier.carrier_name}
                        backgroundColor={"#e4ddfe"}
                        color={"#7c3aed"}
                        hoverBackgroundColor={"#7c3aed"}
                    />
                </Box>
            )}
        </Box>
    );

    const leftContent = (
        <Box
            sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-end",
                flexWrap: "wrap",
            }}
        >
            {!isProcessing && !isFailed && (
                <Box>
                    <StyledToggleButtonGroup
                        value={viewType}
                        exclusive
                        onChange={handleViewTypeChange}
                        size="small"
                    >
                        <ToggleButton value={ScopeViewType.OVERVIEW}>
                            Overview
                        </ToggleButton>
                        <ToggleButton value={ScopeViewType.DETAILS}>
                            Details
                        </ToggleButton>
                    </StyledToggleButtonGroup>
                </Box>
            )}
        </Box>
    );

    const isReady = !isProcessing && !isFailed;

    const actions = (
        <>
            {isReady && onChatOpen && organizationSettings.ai_chat_enabled && (
                <ActionButton
                    icon={<MessageSquare />}
                    onClick={onChatOpen}
                    sx={{
                        backgroundColor: theme.palette.secondary.main,
                        "&:hover": {
                            backgroundColor: theme.palette.secondary.dark,
                        },
                    }}
                >
                    Ask Polly
                </ActionButton>
            )}
            {isReady && (
                <ActionButton
                    icon={<DownloadIcon />}
                    onClick={handleExport}
                    sx={{
                        backgroundColor: theme.palette.primary.main,
                        "&:hover": {
                            backgroundColor: theme.palette.primary.dark,
                        },
                    }}
                >
                    Export
                </ActionButton>
            )}
            <Box sx={{ position: "relative" }}>
                <ActionButton
                    icon={<MoreVertical />}
                    onClick={handleMoreMenuOpen}
                    sx={{
                        backgroundColor: theme.palette.grey[600],
                        "&:hover": {
                            backgroundColor: theme.palette.grey[700],
                        },
                    }}
                >
                    More
                </ActionButton>
                <Menu
                    anchorEl={moreMenuAnchor}
                    open={Boolean(moreMenuAnchor)}
                    onClose={handleMoreMenuClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    sx={{
                        mt: 0.5,
                        "& .MuiPaper-root": {
                            borderRadius: "8px",
                            boxShadow:
                                "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                            minWidth: 220,
                        },
                    }}
                >
                    <MenuItem
                        onClick={handleRenameClick}
                        sx={{
                            fontSize: "0.75rem",
                            py: 1,
                            px: 1.5,
                            gap: 1,
                            "&:hover": {
                                backgroundColor: theme.palette.grey[50],
                            },
                        }}
                    >
                        <Edit
                            style={{
                                width: 14,
                                height: 14,
                                color: theme.palette.text.secondary,
                            }}
                        />
                        Rename This Scope
                    </MenuItem>
                </Menu>
            </Box>
        </>
    );

    return (
        <>
            <PageHeader
                title={scope.document_title}
                subhead={subheadContent}
                leftContent={leftContent}
                titleActions={actions}
                compact
            />

            {/* Rename Modal */}
            <Modal
                open={renameModalOpen}
                onClose={() => setRenameModalOpen(false)}
                title="Rename This Scope"
                actions={[
                    {
                        label: "Rename",
                        onClick: handleRename,
                        variant: "contained",
                        color: "primary",
                        disabled:
                            !newName.trim() ||
                            newName === scope.document_title ||
                            renameMutation.isPending,
                    },
                ]}
            >
                <TextField
                    label="Scope Name"
                    value={newName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setNewName(e.target.value)
                    }
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter") {
                            handleRename();
                        }
                    }}
                    autoFocus
                    sx={{ width: "100%" }}
                />
            </Modal>

            {/* Export Modal */}
            <ExportScopeModal
                open={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                scope={scope}
                onExport={handleExportSubmit}
            />
        </>
    );
};
